import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/toast-context";
import { ScheduleService } from "../../services/schedule-service";
import { CircularProgress } from "@mui/material";

interface DeleteScheduleButtonProps {
  scheduleId: number;
  onScheduleDeleted: () => void;
}

export function DeleteScheduleButton({
  scheduleId,
  onScheduleDeleted,
}: DeleteScheduleButtonProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const scheduleService = useMemo(() => new ScheduleService(), []);
  const [loading, setLoading] = useState(false);

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await scheduleService.deleteSchedule(
        { scheduleId },
        async () => {
          showToast(t("branches.schedules.delete.success"), "success");
          onScheduleDeleted();
        },
        (error) => {
          showToast(error, "error");
        }
      );
    } finally {
      setLoading(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <Button
        title={t("branches.schedules.delete.action")}
        onClick={handleDeleteClick}
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
        }}
      >
        <svg
          fill="#EF0BB8"
          width="16"
          height="16"
          viewBox="0 0 32 32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m19.587 16.001 6.096 6.096a1.015 1.015 0 0 1 0 1.435l-2.151 2.151a1.015 1.015 0 0 1-1.435 0L16 19.587l-6.097 6.096a1.014 1.014 0 0 1-1.434 0l-2.152-2.151a1.015 1.015 0 0 1 0-1.435l6.097-6.096-6.097-6.097a1.015 1.015 0 0 1 0-1.435L8.47 6.318a1.014 1.014 0 0 1 1.434 0L16 12.415l6.097-6.097a1.015 1.015 0 0 1 1.435 0l2.151 2.152a1.015 1.015 0 0 1 0 1.435l-6.096 6.096z" />
        </svg>
      </Button>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby={t("branches.schedules.delete.title")}
        aria-describedby={t("branches.schedules.delete.description")}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "470px",
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: "20px 24px",
            fontWeight: 400,
            fontSize: "16px",
            borderRadius: "30px",
            "&.MuiDialogTitle-root": {
              fontFamily: "Montserrat",
            },
          }}
          id="delete-schedule"
        >
          {t("branches.schedules.delete.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              fontWeight: 400,
            }}
            id="delete-schedule-description"
          >
            {t("branches.schedules.delete.description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              fontFamily: "Montserrat",
              color: "#000",
              fontSize: "0.875rem",
              textTransform: "capitalize",
              fontWeight: 300,
            }}
            onClick={() => setIsDeleteDialogOpen(false)}
            color="inherit"
            disabled={loading}
          >
            {t("branches.schedules.delete.cancel")}
          </Button>
          <Button
            sx={{
              fontFamily: "Montserrat",
              backgroundColor: "#E60032",
              color: "#fff",
              fontSize: "0.875rem",
              textTransform: "capitalize",
              fontWeight: 300,
              width: "90px",
              "&.Mui-disabled": {
                opacity: "40%",
                color: "#fff",
              },
            }}
            onClick={handleDeleteConfirm}
            disabled={loading}
          >
            {!loading ? (
              t("branches.schedules.delete.confirm")
            ) : (
              <CircularProgress
                sx={{
                  color: "#fff",
                }}
                size="1.5rem"
                className="animate-spin "
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
